import React, { useEffect, useState } from "react";
import { useField } from "formik";
import Form from "react-bootstrap/Form";
import clsx from "clsx";
import Select from "react-select";
import { FormSelectProps } from "react-bootstrap";
import { useIntl } from "react-intl";
import { PacmanLoader } from "react-spinners";

type SimpleFormSelectProps = FormSelectProps & {
    label?: string;
    values: SimpleFormSelectOption[];
    name: string;
    isLoading?: boolean;
    cssClass?: string[];
    addEmpty?: boolean;
    disabled?: boolean;
    isClearable?: boolean;
    isMulti?: boolean;
    [key: string]: any;
};

export type SimpleFormSelectOption = { label: string; value: number | string; [key: string]: any };

const LoadingIndicator = () => {
    const [LoadingColor, setLoadingColor] = useState("");

    useEffect(() => {
        setLoadingColor(getComputedStyle(document.body).getPropertyValue("--kt-primary"));
    }, []);
    return <PacmanLoader size={8} color={LoadingColor} />;
};

export const SimpleFormSelect: React.FC<SimpleFormSelectProps> = (props) => {
    const {
        label,
        name,
        cssClass,
        values,
        disabled,
        isLoading = false,
        placeholder,
        isClearable = true,
        isMulti = false,
    } = props;
    const [formikProps, meta, FormikHelpers] = useField(name);
    const { formatMessage } = useIntl();
    /*     const hasValue = values.find((value) => value.value === formikProps.value) || null; */
    let hasValue;
    if (isMulti) {
        if (formikProps.value) {
            hasValue = [];
            formikProps.value.forEach((el: string | number) =>
                hasValue.push(values.find((value) => value.value === el))
            );
        }
    } else {
        hasValue = values.find((value) => value.value === formikProps.value) || null;
    }
    return (
        <div className="mb-2">
            {label && <Form.Label>{label}</Form.Label>}
            <Select
                isMulti={isMulti}
                className={clsx(
                    cssClass || [],
                    {
                        "is-invalid": meta.touched && meta.error,
                    },
                    {
                        "is-valid": meta.touched && !meta.error,
                    }
                )}
                styles={{
                    control: (baseStyles) => ({
                        ...baseStyles,
                        fontSize: "1.1rem",
                        fontWeight: "500",
                        lineHeight: "1.5",
                        minHeight: "43.39px",
                        backgroundColor: "var(--kt-input-solid-bg)",
                        border: meta.error && meta.touched ? "1px solid red" : "unset",
                    }),
                    option: (baseStyles, state) => ({
                        ...baseStyles,
                        backgroundColor: state.isFocused
                            ? "var(--kt-primary)"
                            : "var(--kt-input-solid-bg)",
                        fontWeight: "500",
                        lineHeight: "1.5",
                        fontSize: "0.9rem",
                        color: state.isSelected
                            ? state.isFocused
                                ? "white"
                                : "var(--kt-primary)"
                            : "var(--kt-modal-content-color);",
                    }),
                    singleValue: (baseStyles) => ({
                        ...baseStyles,
                        padding: "3px 5px",
                    }),
                    multiValue: (baseStyles) => ({
                        ...baseStyles,
                        padding: "3px 5px",
                    }),
                    multiValueRemove: (baseStyles) => ({
                        ...baseStyles,
                        ":hover": {
                            backgroundColor: "var(--kt-primary)",
                            color: "white",
                        },
                    }),
                    indicatorSeparator: (baseStyle) => ({
                        ...baseStyle,
                        marginLeft: isLoading ? "24px" : "unset",
                    }),
                }}
                isClearable={!!hasValue && isClearable}
                isLoading={isLoading}
                isDisabled={isLoading || disabled}
                noOptionsMessage={() => formatMessage({ id: "common.select.noOptions" })}
                placeholder={placeholder}
                value={hasValue}
                options={values}
                onBlur={() => FormikHelpers.setTouched(true)}
                components={{ LoadingIndicator }}
                onChange={(newValue) => {
                    let value;
                    if (Array.isArray(newValue)) {
                        value = [...newValue.map((el) => el.value)];
                    } else {
                        if (newValue && "value" in newValue) value = newValue?.value;
                    }
                    FormikHelpers.setValue(value);
                }}
                name={name}
            />
            {meta.error && meta.touched && (
                <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                        <span role="alert">
                            {meta.error.includes("error.")
                                ? formatMessage({ id: meta.error })
                                : meta.error}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};
